import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../lib/api'
import { useToast } from '../ui/ToastProvider'
import { useClaimAdminInsurers } from '../hooks/useClaimAdminInsurers'
import MenuGuideButton from '../components/MenuGuideButton'

const STATUS_LABELS = {
  DRAFT: '임시저장',
  SUBMITTED: '접수됨',
  UNDER_REVIEW: '심사중',
  APPROVED: '승인됨',
  REJECTED: '반려됨',
  COMPLETED: '완료됨',
}

function formatYmd(d) {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function defaultFromTo(daysBack) {
  const to = new Date()
  const from = new Date(to)
  from.setDate(from.getDate() - daysBack)
  return { from: formatYmd(from), to: formatYmd(to) }
}

const numberFormatter = new Intl.NumberFormat('ko-KR')

function formatCount(value) {
  const n = Number(value || 0)
  return numberFormatter.format(Number.isFinite(n) ? n : 0)
}

function formatAverage(value) {
  const n = Number(value || 0)
  return Number.isFinite(n) ? n.toFixed(1) : '0.0'
}

function formatPercent(value) {
  const n = Number(value || 0)
  return `${Number.isFinite(n) ? n.toFixed(1) : '0.0'}%`
}

function percentWidth(value) {
  const n = Number(value || 0)
  if (!Number.isFinite(n)) return '0%'
  return `${Math.max(0, Math.min(100, n))}%`
}

function MetricCard({ label, value, sub, tone = 'slate' }) {
  const toneClass = {
    slate: 'text-slate-900',
    blue: 'text-blue-700',
    emerald: 'text-emerald-700',
    rose: 'text-rose-700',
    amber: 'text-amber-700',
  }[tone] || 'text-slate-900'
  return (
    <div className="rounded border bg-white p-4">
      <div className="text-xs font-semibold text-slate-500">{label}</div>
      <div className={`mt-1 text-2xl font-bold ${toneClass}`}>{value}</div>
      {sub && <div className="mt-1 text-xs text-slate-500">{sub}</div>}
    </div>
  )
}

export default function ClaimStatsPage({ embedded = false }) {
  const { show } = useToast()
  const { allInsurers, orgOptions, loading: insurersLoading } = useClaimAdminInsurers()
  const initial = useMemo(() => defaultFromTo(30), [])
  const [fromDate, setFromDate] = useState(initial.from)
  const [toDate, setToDate] = useState(initial.to)
  const [insurerOrgId, setInsurerOrgId] = useState('')
  const [creatorOrgId, setCreatorOrgId] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  const creatorOrgs = useMemo(() => (orgOptions || [])
    .filter((o) => o?.type === 'GA' || o?.type === 'PERSONAL')
    .sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), 'ko')),
  [orgOptions])

  useEffect(() => {
    let cancelled = false
    const load = async () => {
      setLoading(true)
      try {
        const params = { fromDate, toDate }
        const ins = insurerOrgId === '' ? null : Number(insurerOrgId)
        const cr = creatorOrgId === '' ? null : Number(creatorOrgId)
        if (ins != null && !Number.isNaN(ins)) params.insurerOrgId = ins
        if (cr != null && !Number.isNaN(cr)) params.creatorOrgId = cr
        const res = await api.get('/claims/stats/summary', { params })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!cancelled) setData(payload || null)
      } catch {
        if (!cancelled) {
          setData(null)
          show('통계를 불러오지 못했습니다.', 'error')
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => { cancelled = true }
  }, [fromDate, toDate, insurerOrgId, creatorOrgId, show])

  const applyPresetDays = (d) => {
    const { from, to } = defaultFromTo(d)
    setFromDate(from)
    setToDate(to)
  }

  const periodLabel = useMemo(() => {
    if (!data?.periodFrom || !data?.periodTo) return ''
    try {
      const a = new Date(data.periodFrom)
      const b = new Date(data.periodTo)
      const opts = { dateStyle: 'short', timeZone: 'Asia/Seoul' }
      return `${a.toLocaleDateString('ko-KR', opts)} ~ ${b.toLocaleDateString('ko-KR', opts)}`
    } catch {
      return ''
    }
  }, [data?.periodFrom, data?.periodTo])

  const apps = data?.applications || {}
  const fax = data?.fax || {}
  const intake = data?.intake || {}
  const sig = data?.signatures || {}
  const outcomes = data?.requestOutcomes || {}
  const docs = data?.documents || {}
  const byStatus = apps.byStatus || {}
  const topInsurers = Array.isArray(data?.topInsurersByApplications) ? data.topInsurersByApplications : []
  const daily = Array.isArray(data?.applicationsCreatedByDay) ? data.applicationsCreatedByDay : []
  const totalRequests = Number(outcomes.totalRequests ?? apps.requestedInPeriod ?? apps.createdInPeriod ?? 0)
  const deliveredRequests = Number(outcomes.deliveredRequests ?? fax.deliveredRequestsInPeriod ?? 0)
  const failedRequests = Number(outcomes.failedRequests ?? fax.failedRequestsInPeriod ?? 0)
  const pendingRequests = Number(outcomes.pendingRequests ?? fax.pendingRequestsInPeriod ?? 0)
  const cancelledRequests = Number(outcomes.cancelledRequests ?? fax.cancelledRequestsInPeriod ?? 0)
  return (
    <div className="space-y-4">
      {!embedded ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-bold">청구통계</h1>
              <MenuGuideButton slug="claims" title="보험금청구 가이드" />
            </div>
            {periodLabel && !loading && (
              <p className="mt-1 text-xs text-slate-500">집계 구간(KST 기준): {periodLabel}</p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/ops/forms/claims/templates">청구서식</Link>
            <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/ops/forms/claims/fax">청구번호</Link>
            <Link className="rounded border px-3 py-2 text-sm hover:bg-slate-50" to="/support/claims">보험금 청구</Link>
          </div>
        </div>
      ) : periodLabel && !loading ? (
        <p className="text-xs text-slate-500">집계 구간(KST 기준): {periodLabel}</p>
      ) : null}

      <div className="rounded border bg-white p-4 space-y-3">
        <div className="font-semibold text-slate-900 text-sm">필터</div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">시작일</label>
            <input
              type="date"
              className="rounded border px-2 py-1.5 text-sm"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">종료일</label>
            <input
              type="date"
              className="rounded border px-2 py-1.5 text-sm"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
          </div>
          <div className="flex flex-wrap gap-1">
            <span className="text-xs text-slate-500 self-end pb-2 mr-1">빠른 선택</span>
            {[7, 14, 30, 90, 180].map((d) => (
              <button
                key={d}
                type="button"
                className="rounded border px-2 py-1 text-xs hover:bg-slate-50"
                onClick={() => applyPresetDays(d)}
              >
                {d}일
              </button>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-slate-600 mb-1">보험사</label>
            <select
              className="min-w-[200px] rounded border px-2 py-1.5 text-sm"
              value={insurerOrgId}
              onChange={(e) => setInsurerOrgId(e.target.value)}
              disabled={insurersLoading}
            >
              <option value="">전체</option>
              {allInsurers.map((o) => (
                <option key={o.id} value={String(o.id)}>{o.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-slate-600 mb-1">작성자 소속 조직</label>
            <select
              className="min-w-[200px] rounded border px-2 py-1.5 text-sm"
              value={creatorOrgId}
              onChange={(e) => setCreatorOrgId(e.target.value)}
              disabled={insurersLoading}
            >
              <option value="">전체</option>
              {creatorOrgs.map((o) => (
                <option key={o.id} value={String(o.id)}>{o.name}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="rounded border bg-white p-6 text-slate-500">불러오는 중…</div>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              label="전체 요청"
              value={`${formatCount(totalRequests)}건`}
              sub={`청구서 생성 ${formatCount(apps.createdInPeriod)}건`}
              tone="blue"
            />
            <MetricCard
              label="전송 성공"
              value={`${formatCount(deliveredRequests)}건`}
              sub={`성공률 ${formatPercent(outcomes.deliveredRate)}`}
              tone="emerald"
            />
            <MetricCard
              label="전송 실패"
              value={`${formatCount(failedRequests)}건`}
              sub={`실패율 ${formatPercent(outcomes.failedRate)}`}
              tone="rose"
            />
            <MetricCard
              label="일일 평균 청구수"
              value={`${formatAverage(outcomes.averageRequestsPerDay)}건`}
              sub={`${data.periodDaysInclusive ?? 0}일 기준`}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <MetricCard
              label="평균 첨부 페이지"
              value={`${formatAverage(docs.averageAttachedPagesPerRequest)}장`}
              sub={`전체 ${formatCount(docs.attachedPageCountInPeriod)}장`}
            />
            <MetricCard
              label="평균 첨부 파일"
              value={`${formatAverage(docs.averageAttachedDocumentsPerRequest)}개`}
              sub={`전체 ${formatCount(docs.attachedDocumentCountInPeriod)}개`}
            />
            <MetricCard
              label="전송 대기/진행"
              value={`${formatCount(pendingRequests)}건`}
              sub={`예약 대기 ${formatCount(fax.scheduledNow)}건`}
              tone="amber"
            />
            <MetricCard
              label="청구서 누적"
              value={`${formatCount(apps.totalAllTime)}건`}
              sub={`현재 필터 기준 전체`}
            />
          </div>

          <div className="rounded border bg-white p-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div className="font-semibold text-slate-900">전송 결과 비율</div>
                <div className="mt-1 text-xs text-slate-500">기간 내 전체 요청을 기준으로 현재 확정된 전송 결과를 집계합니다.</div>
              </div>
              <div className="text-xs text-slate-500">취소 {formatCount(cancelledRequests)}건</div>
            </div>
            <div className="mt-4 h-3 w-full overflow-hidden rounded bg-slate-100">
              <div className="flex h-full w-full">
                <div className="bg-emerald-500" style={{ width: percentWidth(outcomes.deliveredRate) }} />
                <div className="bg-rose-500" style={{ width: percentWidth(outcomes.failedRate) }} />
                <div className="bg-amber-400" style={{ width: percentWidth(outcomes.pendingRate) }} />
                <div className="bg-slate-300" style={{ width: percentWidth(outcomes.cancelledRate) }} />
              </div>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
              <div className="flex items-center justify-between rounded bg-emerald-50 px-3 py-2">
                <span className="text-emerald-800">성공</span>
                <span className="font-semibold text-emerald-900">{formatPercent(outcomes.deliveredRate)}</span>
              </div>
              <div className="flex items-center justify-between rounded bg-rose-50 px-3 py-2">
                <span className="text-rose-800">실패</span>
                <span className="font-semibold text-rose-900">{formatPercent(outcomes.failedRate)}</span>
              </div>
              <div className="flex items-center justify-between rounded bg-amber-50 px-3 py-2">
                <span className="text-amber-800">대기/진행</span>
                <span className="font-semibold text-amber-900">{formatPercent(outcomes.pendingRate)}</span>
              </div>
              <div className="flex items-center justify-between rounded bg-slate-50 px-3 py-2">
                <span className="text-slate-700">취소</span>
                <span className="font-semibold text-slate-900">{formatPercent(outcomes.cancelledRate)}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="rounded border bg-white p-4">
              <div className="font-semibold text-slate-900">고객 인테이크 (기간)</div>
              <div className="mt-2 text-sm text-slate-700 space-y-1">
                <div>세션 생성: <span className="font-semibold">{intake.sessionsCreatedInPeriod ?? 0}</span></div>
                <div>최종 완료: <span className="font-semibold">{intake.sessionsFinalizedInPeriod ?? 0}</span></div>
              </div>
            </div>
            <div className="rounded border bg-white p-4">
              <div className="font-semibold text-slate-900">서명 요청 (기간)</div>
              <div className="mt-2 text-sm text-slate-700">
                생성 건수: <span className="font-semibold">{sig.requestsCreatedInPeriod ?? 0}</span>
              </div>
              <div className="mt-2 text-xs text-slate-500">
                가상팩스 필수로 설정된 보험사 수(전역): <span className="font-semibold">{data.insurersWithVirtualFaxRequired ?? 0}</span>
              </div>
            </div>
          </div>

          <div className="rounded border bg-white p-4">
            <div className="font-semibold text-slate-900">청구서 상태 (누적·필터)</div>
            <p className="mt-1 text-xs text-slate-500">선택한 보험사·작성 조직 조건에 맞는 전체 건수입니다.</p>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
              {Object.entries(byStatus).map(([k, v]) => (
                <div key={k} className="flex justify-between rounded bg-slate-50 px-3 py-2">
                  <span className="text-slate-600">{STATUS_LABELS[k] || k}</span>
                  <span className="font-semibold text-slate-900">{v}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="rounded border bg-white p-4 overflow-x-auto">
            <div className="font-semibold text-slate-900 mb-2">보험사별 요청 건수 (기간·필터)</div>
            <table className="min-w-[400px] w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-4">보험사</th>
                  <th className="py-2">요청 건수</th>
                </tr>
              </thead>
              <tbody>
                {topInsurers.length === 0 && (
                  <tr><td colSpan={2} className="py-4 text-slate-500">데이터 없음</td></tr>
                )}
                {topInsurers.map((row) => (
                  <tr key={row.insurerOrgId} className="border-b last:border-b-0">
                    <td className="py-2 pr-4">{row.insurerName}</td>
                    <td className="py-2 font-medium">{formatCount(row.requestCount ?? row.applicationCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded border bg-white p-4 overflow-x-auto">
            <div className="font-semibold text-slate-900 mb-2">일별 청구 요청 (기간·필터)</div>
            <table className="min-w-[280px] w-full text-sm">
              <thead>
                <tr className="border-b text-left">
                  <th className="py-2 pr-4">일자</th>
                  <th className="py-2">요청 건수</th>
                </tr>
              </thead>
              <tbody>
                {daily.length === 0 && (
                  <tr><td colSpan={2} className="py-4 text-slate-500">데이터 없음</td></tr>
                )}
                {daily.map((row) => (
                  <tr key={row.date} className="border-b last:border-b-0">
                    <td className="py-2 pr-4">{row.date}</td>
                    <td className="py-2 font-medium">{formatCount(row.requests ?? row.applicationsCreated)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
